import { useClerk, useSignIn } from '@clerk/react'
import { AlertTriangleIcon } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { Logo } from '@/components/marca/Logo'
import { Boton } from '@/components/ui/Boton'
import { modoTestHabilitado } from '@/lib/entorno'
import { IngresoDePrueba } from './IngresoDePrueba'
import { olvidarUltimoProyecto } from './useSesion'

/**
 * Estados de la tarjeta (§8.1). La verificación de cuenta llega en la Fase 6.
 *  · `normal`         un solo botón: Continuar con Google.
 *  · `sin_proyectos`  entró, pero el mail no tiene proyecto habilitado.
 *  · `cancelado`      canceló el consentimiento de Google.
 *  · `revocado`       el acceso cambió durante la sesión.
 */
export type EstadoIngreso =
  | { tipo: 'normal' }
  | { tipo: 'sin_proyectos'; email: string }
  | { tipo: 'cancelado' }
  | { tipo: 'revocado' }

interface IngresoProps {
  estado?: EstadoIngreso
}

export const MENSAJES = {
  sinProyectos: (email: string) =>
    `Entraste como ${email}, pero este mail todavía no tiene un proyecto habilitado. Pedile a tu contacto en RASTRO que te invite.`,
  cancelado: 'Necesitamos que entres con Google para mostrarte el contenido.',
  revocado: 'Tu acceso a este proyecto cambió. Entrá de nuevo.',
} as const

/**
 * P00 · Ingreso. Tarjeta centrada con el primary logo, "Entrá a tu portal" y
 * un único botón. No hay campo de mail, ni contraseña, ni registro (§8.1,
 * diferencia 9 del §11). Pie con el lema y el año, calculado.
 */
export function Ingreso({ estado = { tipo: 'normal' } }: IngresoProps) {
  return (
    <MarcoIngreso>
      <Tarjeta estado={estado} />
    </MarcoIngreso>
  )
}

/**
 * La tarjeta y el pie de la P00, sin dependencia de Clerk: también la usa
 * el estado "sin configurar" del build sin clave.
 */
export function MarcoIngreso({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-dvh flex-col px-4 py-6 sm:py-10">
      <div className="flex flex-1 items-center justify-center">
        <section
          aria-labelledby="ingreso-titulo"
          className="w-full max-w-md rounded-tarjeta bg-tarjeta px-6 py-8 shadow-tarjeta sm:px-10 sm:py-10"
        >
          <Logo variante="primario" className="mx-auto h-11" />

          <h1
            id="ingreso-titulo"
            className="mt-6 text-center text-2xl font-semibold tracking-tight"
          >
            Entrá a tu portal
          </h1>

          <div className="mt-6">{children}</div>
        </section>
      </div>

      <footer className="mx-auto mt-8 flex w-full max-w-5xl items-center justify-between border-t border-borde pt-4 text-sm text-tinta-suave">
        <span>Menos ruido, más historias</span>
        <span>{new Date().getFullYear()}</span>
      </footer>
    </main>
  )
}

function Tarjeta({ estado }: { estado: EstadoIngreso }) {
  switch (estado.tipo) {
    case 'sin_proyectos':
      return (
        <>
          <Aviso tono="info">{MENSAJES.sinProyectos(estado.email)}</Aviso>
          <BotonSalir>Salir y probar con otra cuenta</BotonSalir>
        </>
      )
    case 'cancelado':
      return (
        <>
          <Aviso tono="info">{MENSAJES.cancelado}</Aviso>
          <BotonGoogle />
        </>
      )
    case 'revocado':
      return (
        <>
          <Aviso tono="atencion">{MENSAJES.revocado}</Aviso>
          <BotonGoogle salirAntes />
        </>
      )
    default:
      return <BotonGoogle />
  }
}

function Aviso({ tono, children }: { tono: 'info' | 'atencion'; children: ReactNode }) {
  return (
    <p
      role="status"
      data-aviso={tono}
      className={
        tono === 'atencion'
          ? 'mb-5 flex gap-2.5 rounded-2xl bg-cambios px-4 py-3 text-sm text-cambios-tinta'
          : 'mb-5 rounded-2xl bg-lienzo px-4 py-3 text-sm text-tinta'
      }
    >
      {tono === 'atencion' && (
        <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
      )}
      <span>{children}</span>
    </p>
  )
}

/** Continuar con Google: el único control de la pantalla. */
function BotonGoogle({ salirAntes = false }: { salirAntes?: boolean }) {
  const { signIn, fetchStatus } = useSignIn()
  const clerk = useClerk()
  const [ocupado, setOcupado] = useState(false)

  const continuar = async () => {
    setOcupado(true)
    try {
      if (salirAntes) await clerk.signOut()
      const { error } = await signIn.sso({
        strategy: 'oauth_google',
        redirectUrl: '/',
        redirectCallbackUrl: '/sso-callback',
      })
      if (error) throw error
    } catch (error) {
      console.error('[ingreso]', error)
      setOcupado(false)
    }
  }

  return (
    <>
      <Boton
        className="w-full"
        disabled={!clerk.loaded || fetchStatus === 'fetching' || ocupado}
        onClick={() => void continuar()}
      >
        <IconoGoogle />
        Continuar con Google
      </Boton>
      <p className="mt-4 text-center text-sm text-tinta-suave">
        Usá la cuenta de Google con la que te invitaron.
      </p>
      {modoTestHabilitado && <IngresoDePrueba />}
    </>
  )
}

function BotonSalir({ children }: { children: ReactNode }) {
  const clerk = useClerk()
  const navigate = useNavigate()
  return (
    <Boton
      className="w-full"
      variante="secundario"
      onClick={() => {
        olvidarUltimoProyecto()
        // Sin la redirección de Clerk: en Chrome de Android dejaba la página en blanco.
        void clerk.signOut().then(() => navigate('/ingreso', { replace: true }))
      }}
    >
      {children}
    </Boton>
  )
}

function IconoGoogle() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
      <path
        fill="currentColor"
        d="M21.6 12.2c0-.7-.1-1.3-.2-1.9H12v3.7h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.3Z"
      />
      <path
        fill="currentColor"
        opacity=".8"
        d="M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22Z"
      />
      <path
        fill="currentColor"
        opacity=".6"
        d="M6.4 14a6 6 0 0 1 0-3.9V7.5H3.1a10 10 0 0 0 0 9l3.3-2.5Z"
      />
      <path
        fill="currentColor"
        opacity=".9"
        d="M12 6c1.5 0 2.8.5 3.8 1.5l2.9-2.9A10 10 0 0 0 3.1 7.5l3.3 2.6C7.2 7.8 9.4 6 12 6Z"
      />
    </svg>
  )
}
